import React from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
} from '@mui/material';
import BridgeBankLogo from './BridgeBankLogo';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Assessment as AssessmentIcon,
  PersonAdd as PersonAddIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  AccountTree as DecisioningIcon,
  CloudSync as DataIngestionIcon,
  Psychology as ModelIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { RootState, AppDispatch } from '../store';

import { logout } from '../store/authSlice';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import ExpandableReportsSidebar from './ExpandableReportsSidebar';

const drawerWidth = 240;

interface Props {
  children: React.ReactNode;
}

const Layout: React.FC<Props> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const auth = useSelector((state: RootState) => state.auth);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const { t } = useLanguage();

  // Role- and privilege-based navigation items
  const getNavigationItems = () => {
    const baseItems = [
      { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/', roles: ['merchant', 'wave', 'bank'], requiredPrivileges: undefined },
      { text: t('nav.applicationStatus'), icon: <CheckCircleIcon />, path: '/status', roles: ['merchant', 'wave', 'bank'], requiredPrivileges: ['applications:read'] },
      { text: t('nav.transactionDetails'), icon: <AssessmentIcon />, path: '/transactions', roles: ['merchant', 'wave', 'bank'], requiredPrivileges: ['transactions:read'] },
      { text: t('nav.auditLog'), icon: <HistoryIcon />, path: '/audit-log', roles: ['merchant', 'wave', 'bank'], requiredPrivileges: ['reports:read'] },
    ];

    const adminItems = [
      { text: t('nav.rulesManagement'), icon: <GavelIcon />, path: '/rules', roles: ['bank'], requiredPrivileges: ['rules:read'] },
      { text: 'Decisioning Engine', icon: <DecisioningIcon />, path: '/decisioning', roles: ['bank'], requiredPrivileges: ['rules:read'] },
      { text: 'Data Ingestion', icon: <DataIngestionIcon />, path: '/data-ingestion', roles: ['bank'], requiredPrivileges: ['reports:read'] },
      { text: 'Model Performance', icon: <ModelIcon />, path: '/model-performance', roles: ['bank'], requiredPrivileges: ['reports:read'] },
      { text: t('nav.apiDocs'), icon: <DescriptionIcon />, path: '/api-docs', roles: ['bank'], requiredPrivileges: ['rules:read'] },
      { text: t('nav.usersManagement'), icon: <PersonAddIcon />, path: '/admin/users', roles: ['bank'], requiredPrivileges: ['users:read'] },
      { text: t('nav.notifications'), icon: <DescriptionIcon />, path: '/admin/notifications', roles: ['bank'], requiredPrivileges: ['reports:read'] },
    ];

    const allItems = [...baseItems, ...adminItems];
    const userRole = auth.user?.role;
    const userPrivileges = auth.user?.privileges;

    if (!userRole) return baseItems;

    return allItems.filter(item => {
      if (!item.roles.includes(userRole)) return false;
      if (!item.requiredPrivileges || item.requiredPrivileges.length === 0) return true;
      if (userPrivileges === undefined) return true;
      const hasOne = item.requiredPrivileges.some(p => userPrivileges.includes(p));
      return hasOne;
    });
  };

  const mainNavItems = getNavigationItems();

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      background: 'linear-gradient(180deg, #212121 0%, #424242 100%)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Toolbar sx={{ 
        bgcolor: 'transparent',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80px',
        color: 'white',
        flexShrink: 0
      }}>
        <BridgeBankLogo height={50} />
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      
      {/* Scrollable navigation area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        pb: 2,
        background: 'linear-gradient(180deg, #212121 0%, #424242 100%)'
      }}>
        <List sx={{ pt: 2 }}>
          {mainNavItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5, px: 1 }}>
              <ListItemButton
                component={NavLink}
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{
                  borderRadius: 2,
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: 'white',
                  },
                  '&.active': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    }
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} sx={{ '& .MuiTypography-root': { fontSize: '0.9rem', fontWeight: 'inherit' } }} />
              </ListItemButton>
            </ListItem>
          ))}
          
          {/* Expandable Reports Section */}
          <ExpandableReportsSidebar />
        </List>
      </Box>
      
      {/* Fixed logout button at bottom */}
      <Box sx={{ 
        flexShrink: 0, 
        p: 1,
        borderTop: '1px solid rgba(255,255,255,0.12)',
        background: 'linear-gradient(180deg, #212121 0%, #424242 100%)'
      }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.08)',
                color: 'white',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary={t('nav.logout')} sx={{ '& .MuiTypography-root': { fontSize: '0.9rem' } }} />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box sx={{ display: { xs: 'block', sm: 'none' }, mr: 2 }}>
              <BridgeBankLogo height={30} />
            </Box>
            <Typography variant="h6" noWrap component="div" sx={{ color: 'white', fontWeight: 'bold' }}>
              {matchPath('/application/:id', location.pathname)
                ? t('nav.applicationDetails')
                : mainNavItems.find(item => item.path === location.pathname)?.text || t('nav.dashboard')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', display: { xs: 'none', md: 'block' } }}>
              {auth.user?.role?.toUpperCase() || t('nav.userRole')} | {auth.user?.email || 'admin@finscore.ai'}
            </Typography>
            <LanguageSwitcher />
            <IconButton sx={{ ml: 1 }} onClick={() => navigate('/settings')} color="inherit" title={t('nav.settingsTooltip')}>
              <SettingsIcon />
            </IconButton>
            <IconButton sx={{ ml: 1 }} onClick={handleLogout} color="inherit" title={t('nav.logoutTooltip')}>
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          height: '100vh',
          overflow: 'auto',
          background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        }}
      >
        <Toolbar />
        <div style={{ padding: 24 }}>
          {children}
        </div>
      </Box>
    </Box>
  );
}

export default Layout; 